// src/pages/profile-settings/patient/OrganDonor.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Card from "@/components/ui/Card.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

/**
 * Patient Settings → Organ donor
 * Simple yes/no preference.
 * - No search, just a short explainer + 2 options
 * - Ghost Save on entry, wakes when value differs from initial
 * - Unsaved-changes confirm
 * - Persists via patientIntroPreview.organDonor ("Yes" | "No")
 */
const OPTIONS = [
  { id: "yes", stored: "Yes", key: "common.yes", fallback: "Yes" },
  { id: "no", stored: "No", key: "common.no", fallback: "No" },
];

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function normalizeStoredValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "yes" || normalized === "true") return "yes";
  if (normalized === "no" || normalized === "false") return "no";
  return "";
}

function toStoredValue(optionId) {
  return OPTIONS.find((option) => option.id === optionId)?.stored || "";
}

export default function OrganDonorSettings() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);

  // Seed from preview
  const introPreview = React.useMemo(() => loadPatientIntroPreview() || {}, []);
  const initialValue = normalizeStoredValue(introPreview.organDonor);

  const [seedValue, setSeedValue] = React.useState(initialValue);
  const [local, setLocal] = React.useState(initialValue);
  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  React.useEffect(() => {
    setSeedValue(initialValue);
    setLocal(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDbIfNeeded() {
      if (initialValue) return;

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const { data: row, error: rowErr } = await supabase
        .from("patient_profiles")
        .select("organ_donor")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!alive || rowErr || !row) return;

      const dbValue =
        typeof row.organ_donor === "boolean"
          ? row.organ_donor
            ? "yes"
            : "no"
          : "";
      if (!dbValue) return;

      setSeedValue(dbValue);
      setLocal(dbValue);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        organDonor: toStoredValue(dbValue),
      });
    }

    hydrateFromDbIfNeeded();
    return () => {
      alive = false;
    };
  }, [initialValue]);

  const dirty = local !== seedValue;
  const canSave = dirty && !!local;

  const toggle = (choice) => {
    setLocal((prev) => (prev === choice ? "" : choice)); // tap again to clear
  };

  async function handleSave() {
    if (!canSave) return;

    try {
      const current = loadPatientIntroPreview() || {};
      const next = {
        ...current,
        organDonor: toStoredValue(local),
      };
      savePatientIntroPreview(next);

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const organDonor =
          local === "yes" ? true : local === "no" ? false : null;
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              organ_donor: organDonor,
            },
            { onConflict: "user_id" }
          );
        if (upErr) throw upErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (err) {
      console.error("[OrganDonorSettings] Save error:", err);
      setSaveError(
        t(
          "settings.edit.common.saveFailed",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        )
      );
    }
  }

  function handleClose() {
    if (!dirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.edit.organDonor.title", isFrench ? "Don d’organes" : "Organ donor")}
        onBack={handleClose}
        close
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <p className="mt-1 mb-4 text-body-medium text-on-surface-var">
        {t(
          "settings.edit.organDonor.subtitle",
          isFrench
            ? "Indiquez si vous êtes enregistré comme donneur d’organes. Vous pouvez modifier cette préférence à tout moment et en discuter avec votre médecin."
            : "Choose whether you are registered as an organ donor. You can change this preference anytime and discuss it with your doctor."
        )}
      </p>

      <Divider variant="outline" full className="mb-6" />

      <Card className="relative z-10 overflow-hidden">
        {OPTIONS.map((option, idx) => {
          const checked = local === option.id;
          return (
            <div key={option.id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(option.id)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle(option.id)
                }
                className="w-full flex items-center"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <div className="flex-1 pl-5 pr-4 py-4 text-left">
                  <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                    {t(option.key, isFrench ? (option.id === "yes" ? "Oui" : "Non") : option.fallback)}
                  </span>
                </div>
                <div className="mr-4">
                  <Checkbox
                    checked={checked}
                    onChange={(next) => {
                      if (next !== checked) toggle(option.id);
                    }}
                  />
                </div>
              </div>

              {idx < OPTIONS.length - 1 && (
                <Divider inset variant="outlineVariant" />
              )}
            </div>
          );
        })}
      </Card>

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowConfirmLeave(false)}
          />
          <div
            className="relative w-[312px] max-w-[560px] min-w-[288px] bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                       shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]"
          >
            <div className="px-6 pt-6">
              <div className="text-headline-small text-on-surface">
                {t("settings.edit.organDonor.discardTitle", isFrench ? "Abandonner les modifications ?" : "Discard changes?")}
              </div>
              <div className="text-body-medium text-on-surface-var mt-4">
                {t(
                  "settings.edit.organDonor.discardBody",
                  isFrench
                    ? "Vous avez des modifications non enregistrées concernant le don d’organes. Voulez-vous fermer sans enregistrer ?"
                    : "You have unsaved organ donor preference changes. Are you sure you want to close without saving?"
                )}
              </div>
            </div>

            <div className="p-4 flex gap-3">
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  color: "rgb(var(--afrimed-primary))",
                }}
                onClick={() => setShowConfirmLeave(false)}
              >
                {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
                  color: "var(--Schemes-Critical,#F8533A)",
                }}
                onClick={() => {
                  setShowConfirmLeave(false);
                  nav(PATIENT_SETTINGS_PROFILE_ROUTE);
                }}
              >
                {t("common.discard", isFrench ? "Abandonner" : "Discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global “Changes saved” Snackbar */}
      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
